import type {
  ApplicationStatus,
  CustomerApplication,
  CustomerFilingStatus,
  CustomerRecordStatus,
  ManagedCustomer,
  SalesUser,
  SalesUserImportResult,
} from '@/lib/business-workflow'
import { z } from 'zod'
import {
  CustomerFieldBindingConfidenceHintSchema,
  CustomerFieldBindingOverwritePolicySchema,
  CustomerFieldBindingTransformSchema,
  CustomerFieldMappingTargetSchema,
} from './customer-application-binding'
import type {
  CustomerApplicationFieldValueContract,
  CustomerApplicationSourceSummaryContract,
  CustomerFieldMappingTarget,
} from './customer-application-binding'
import type { ContractSourceMeta } from './questionnaire'

export {
  CustomerFieldMappingTargetSchema,
  customerFieldTargetOptions,
} from './customer-application-binding'
export type {
  CustomerFieldBindingConfidenceHint,
  CustomerFieldBindingOverwritePolicy,
  CustomerFieldBindingTransform,
  CustomerFieldMappingGroup,
  CustomerFieldMappingTarget,
} from './customer-application-binding'

export const ApplicationStatusSchema = z.enum([
  'draft',
  'pending_review',
  'changes_requested',
  'approved',
  'approved_pending_erp_filing',
  'customer_created',
  'duplicate_pending',
  'rejected',
])

export const CustomerFilingStatusSchema = z.enum(['not_filed', 'filed'])
export const CustomerRecordStatusSchema = z.enum(['active', 'archived'])
export const SalesUserRoleSchema = z.enum(['sales', 'assistant', 'manager'])
export const SalesUserStatusSchema = z.enum(['active', 'inactive', 'blocked'])
export const SalesUserImportFormatSchema = z.enum(['csv', 'xlsx', 'excel'])
export const SalesUserFileImportFormatSchema = z.enum(['pdf', 'png', 'jpg', 'jpeg', 'webp'])
export const SalesUserFileImportContentTypeSchema = z.enum([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
])
export const SalesUserFileImportLanguageHintSchema = z.enum(['zh-TW', 'en', 'mixed'])

export type SalesUserImportFormat = z.infer<typeof SalesUserImportFormatSchema>
export type SalesUserFileImportFormat = z.infer<typeof SalesUserFileImportFormatSchema>
export type SalesUserFileImportContentType = z.infer<typeof SalesUserFileImportContentTypeSchema>

export const SalesUserFileImportPreviewRequestSchema = z
  .object({
    format: z.string().min(1),
    fileName: z.string().trim().min(1).max(255),
    contentType: z.string().trim().min(1),
    sizeBytes: z.number().int().positive(),
    checksumSha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/i)
      .optional(),
    languageHint: SalesUserFileImportLanguageHintSchema.optional(),
    storageKey: z.string().trim().min(1).max(512).optional(),
  })
  .strict()

export type SalesUserFileImportPreviewRequest = z.infer<
  typeof SalesUserFileImportPreviewRequestSchema
>

export const SalesUserFileImportConfirmRequestSchema = z
  .object({
    previewId: z.string().trim().min(1).max(128),
    correctedRowsChecksumSha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/i)
      .optional(),
    rowCount: z.number().int().nonnegative().max(500).optional(),
  })
  .strict()

export type SalesUserFileImportConfirmRequest = z.infer<
  typeof SalesUserFileImportConfirmRequestSchema
>

export type CustomerApplicationContract = CustomerApplication & {
  duplicateSignals: DuplicateCustomerSignal[]
  sourceSummary?: CustomerApplicationSourceSummaryContract
  fieldValues?: CustomerApplicationFieldValueContract[]
  sourceMeta: ContractSourceMeta
}

export type SupervisorReviewAction = 'approve' | 'return' | 'detail'

export type SupervisorReviewEmailProviderState =
  | 'send_disabled'
  | 'provider_not_configured'
  | 'missing_provider_secret'
  | 'ready_for_live_provider'

export type SupervisorReviewEmailSkipReason =
  | 'email_send_disabled'
  | 'provider_not_configured'
  | 'provider_secret_missing'
  | 'live_sender_adapter_not_enabled'
  | 'missing_supervisor_email'
  | 'review_action_token_schema_missing'

export type SupervisorReviewEmailActionLinkContract = {
  action: SupervisorReviewAction
  label: string
  confirmationUrl: string
  /** Legacy compatibility field. Empty string means no expiration is configured. */
  expiresAt: string
  tokenId: string
}

export type SupervisorReviewEmailPreviewContract = {
  mode: 'preview_only'
  providerMode: 'mock' | 'live'
  providerState: SupervisorReviewEmailProviderState
  sendEnabled: boolean
  sendSkipped: boolean
  skipReason: SupervisorReviewEmailSkipReason
  liveSendAllowed: boolean
  to: string
  subject: string
  htmlPreview: string
  actionLinks: SupervisorReviewEmailActionLinkContract[]
  audit: {
    event: 'supervisor_review_email.preview_created'
    applicationId: string
    supervisorEmail: string
    tokenPersistence?: 'memory' | 'd1_hash' | 'd1_schema_missing'
    tokenBindings: Array<{
      tokenId: string
      applicationId: string
      action: SupervisorReviewAction
      supervisorEmail?: string
      recipientEmailHash?: string
      /** Legacy compatibility field. Empty string means no expiration is configured. */
      expiresAt: string
    }>
    rawTokenLogged: false
    source: 'synthetic' | 'database'
  }
}

export type SupervisorReviewConfirmationContract = {
  applicationId: string
  action: SupervisorReviewAction
  status: ApplicationStatus
  companyName: string
  contactEmail: string
  supervisorEmail: string
  supervisorName: string
  /** Legacy compatibility field. Empty string means no expiration is configured. */
  expiresAt: string
  confirmationRequired: true
  backofficeLoginPath?: string
  answers?: Array<{
    questionTitle: string
    questionType: string
    answer: string
    mappedField?: string
  }>
}

export type SalesUserImportResultContract = SalesUserImportResult & {
  sourceMeta: ContractSourceMeta
}

export type SalesUserFileImportPreviewDisabledMeta = ContractSourceMeta & {
  provider: 'sales_user_file_import'
  providerState: 'disabled' | 'adapter_not_enabled'
  fileImportProviderMode: 'disabled' | 'live'
  aiProviderMode: 'disabled' | 'mock' | 'live'
  fileUploadProviderMode: 'disabled' | 'mock' | 'live'
  extraction: 'disabled'
  storage: 'disabled'
  accountCreation: 'disabled'
  manualCorrectionRequired: true
  acceptedMetadata: Pick<
    SalesUserFileImportPreviewRequest,
    'format' | 'fileName' | 'contentType' | 'sizeBytes' | 'checksumSha256' | 'languageHint'
  >
}

export type SalesUserFileImportConfirmDisabledMeta = ContractSourceMeta & {
  provider: 'sales_user_file_import'
  providerState: 'disabled' | 'adapter_not_enabled'
  fileImportProviderMode: 'disabled' | 'live'
  aiProviderMode: 'disabled' | 'mock' | 'live'
  fileUploadProviderMode: 'disabled' | 'mock' | 'live'
  confirmation: 'disabled'
  storage: 'disabled'
  accountCreation: 'disabled'
  rawPayloadIncluded: false
  acceptedMetadata: SalesUserFileImportConfirmRequest
}

export type CustomerProfileContract = ManagedCustomer & {
  sourceMeta: ContractSourceMeta
}

export const ErpCustomerBackfillFieldSchema = z.enum([
  'erpCustomerCode',
  'companyName',
  'taxId',
  'industry',
  'companySize',
  'website',
  'companyAddress',
  'companyPhone',
  'contactName',
  'contactTitle',
  'contactEmail',
  'contactPhone',
  'decisionRole',
  'paymentTerms',
  'creditLimit',
  'salesRegion',
  'expectedValue',
  'needSummary',
  'internalNotes',
])

export const ErpCustomerBackfillCompareRequestSchema = z.object({
  erpCustomerCode: z.string().trim().min(1).max(120),
})

export const ErpCustomerBackfillComparisonStateSchema = z.enum(['new', 'changed', 'same', 'empty'])

export const ErpCustomerBackfillFieldComparisonSchema = z.object({
  field: ErpCustomerBackfillFieldSchema,
  label: z.string(),
  currentValue: z.string(),
  erpValue: z.string(),
  state: ErpCustomerBackfillComparisonStateSchema,
  selectable: z.boolean(),
})

export const ErpCustomerBackfillCompareContractSchema = z.object({
  customerId: z.string(),
  erpCustomerCode: z.string(),
  message: z.string(),
  providerState: z.enum(['configured', 'provider_not_configured', 'provider_failed']),
  source: z.enum(['miaolin_erp_customer_lookup_webhook', 'provider_not_configured']),
  fields: z.array(ErpCustomerBackfillFieldComparisonSchema),
})

export type ErpCustomerBackfillField = z.infer<typeof ErpCustomerBackfillFieldSchema>
export type ErpCustomerBackfillCompareRequest = z.infer<
  typeof ErpCustomerBackfillCompareRequestSchema
>
export type ErpCustomerBackfillComparisonState = z.infer<
  typeof ErpCustomerBackfillComparisonStateSchema
>
export type ErpCustomerBackfillFieldComparison = z.infer<
  typeof ErpCustomerBackfillFieldComparisonSchema
>
export type ErpCustomerBackfillCompareContract = z.infer<
  typeof ErpCustomerBackfillCompareContractSchema
>

export const ReviewNotificationJobStatusSchema = z.enum([
  'pending',
  'provider_not_configured',
  'sent',
  'failed',
  'retryable_error',
])

export const ReviewNotificationJobContractSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  formId: z.string().nullable(),
  status: ReviewNotificationJobStatusSchema,
  attemptCount: z.number().int().nonnegative(),
  lastErrorCode: z.string().nullable().optional(),
  scheduledAt: z.string().nullable().optional(),
  sentAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  recipientEmail: z.string(),
  salesManagerName: z.string(),
  form_type: z.string(),
  customer_short_name: z.string(),
  submitted_by: z.string(),
  detailLink: z.string(),
  approvalLink: z.string(),
  providerDeliveryStatus: z.string().optional(),
  hasMessageId: z.boolean().optional(),
  message: z.string().optional(),
})

export type ReviewNotificationJobStatus = z.infer<typeof ReviewNotificationJobStatusSchema>
export type ReviewNotificationJobContract = z.infer<typeof ReviewNotificationJobContractSchema>

export type ReviewNotificationJobProcessResult = {
  processed: boolean
  status: ReviewNotificationJobStatus | 'idle'
  providerState: 'configured' | 'failed' | 'not_configured'
  providerDeliveryStatus?: string
  attemptCount: number
  message?: string
  nextScheduledAt?: string | null
}

export type ReviewNotificationJobBatchProcessResult = {
  processedCount: number
  limit: number
  idle: boolean
  results: ReviewNotificationJobProcessResult[]
}

export type CustomerContactContract = {
  id: string
  customerId: string
  name: string
  title: string
  email: string
  phone: string
  isPrimary: boolean
}

export const CustomerFieldMappingContractSchema = z.object({
  id: z.string(),
  formId: z.string(),
  questionId: z.string(),
  questionTitle: z.string(),
  targetField: CustomerFieldMappingTargetSchema,
  targetLabel: z.string().optional(),
  dbFieldName: z.string().optional(),
  dataType: z.string().optional(),
  specQuestionNo: z.number().int().positive().optional(),
  choiceRuleSummary: z.string().optional(),
  validationRuleSummary: z.string().optional(),
  transform: CustomerFieldBindingTransformSchema.optional(),
  requiredForApproval: z.boolean(),
  overwritePolicy: CustomerFieldBindingOverwritePolicySchema.optional(),
  confidenceHint: CustomerFieldBindingConfidenceHintSchema.optional(),
  updatedAt: z.string().optional(),
})

export type CustomerFieldMappingContract = z.infer<typeof CustomerFieldMappingContractSchema>

export type DuplicateCustomerSignal = {
  field:
    | 'customer_code'
    | 'customer_short_name'
    | 'companyPhone'
    | 'taxId'
    | 'companyName'
    | 'contactEmail'
    | 'contactPhone'
  value: string
  matchedCustomerId: string
  matchedCustomerName: string
  matchedCustomerCode?: string
  confidence: number
  source?:
    | 'd1_project_customer'
    | 'erp_customer_webhook'
    | 'miaolin_customer_check_webhook'
    | string
  safeMessage?: string
}

export type DuplicateResolutionMode = 'merge_preview' | 'new_customer'

export function createDuplicateSignals(
  application: Pick<CustomerApplication, 'taxId' | 'companyName'> &
    Partial<Pick<CustomerApplication, 'contactEmail' | 'contactPhone' | 'answers' | 'source'>>,
  customers: Array<
    Pick<
      ManagedCustomer,
      'id' | 'companyName' | 'taxId' | 'contactEmail' | 'contactPhone' | 'erpCustomerCode'
    >
  >,
): DuplicateCustomerSignal[] {
  if (!isFullNewCustomerDuplicateCandidate(application)) return []

  return customers.flatMap((customer) => {
    const signals: DuplicateCustomerSignal[] = []
    if (application.taxId && customer.taxId === application.taxId) {
      signals.push({
        field: 'taxId',
        value: application.taxId,
        matchedCustomerId: customer.id,
        matchedCustomerName: customer.companyName,
        matchedCustomerCode: customer.erpCustomerCode || undefined,
        confidence: 0.98,
        source: 'd1_project_customer',
      })
    }
    if (
      application.companyName &&
      customer.companyName.toLocaleLowerCase() === application.companyName.toLocaleLowerCase()
    ) {
      signals.push({
        field: 'companyName',
        value: application.companyName,
        matchedCustomerId: customer.id,
        matchedCustomerName: customer.companyName,
        matchedCustomerCode: customer.erpCustomerCode || undefined,
        confidence: 0.92,
        source: 'd1_project_customer',
      })
    }
    return signals
  })
}

function isFullNewCustomerDuplicateCandidate(
  application: Partial<Pick<CustomerApplication, 'answers' | 'source'>>,
) {
  if (!application.answers?.length) return false
  const formTypeAnswer =
    application.answers.find((answer) =>
      normalizeFlowAnswer(answer.questionTitle).includes(normalizeFlowAnswer('新客戶建檔選擇')),
    )?.answer ?? application.source
  return normalizeFlowAnswer(formTypeAnswer).includes(normalizeFlowAnswer('全新客戶'))
}

function normalizeFlowAnswer(value: string | undefined) {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase()
    .replace(/\s+/g, '')
}

export const ListCustomerApplicationsParamsSchema = z.object({
  keyword: z.string().optional(),
  formId: z.string().optional(),
  ownerEmail: z.string().optional(),
  supervisorEmail: z.string().optional(),
  status: z.union([z.literal('all'), ApplicationStatusSchema]).default('all'),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
})
export type ListCustomerApplicationsParams = z.input<typeof ListCustomerApplicationsParamsSchema>

export type ListCustomerApplicationsResult = {
  data: CustomerApplicationContract[]
  meta: ContractSourceMeta & {
    total: number
    page: number
    limit: number
  }
}

export const CustomerWorkflowFilterSchema = z.enum([
  'all',
  'new_customer',
  'existing_customer_change',
])
export type CustomerWorkflowFilter = z.infer<typeof CustomerWorkflowFilterSchema>

export const ListCustomersParamsSchema = z.object({
  keyword: z.string().optional(),
  formId: z.string().optional(),
  ownerEmail: z.string().optional(),
  status: z.union([z.literal('all'), CustomerRecordStatusSchema]).default('active'),
  filingStatus: z.union([z.literal('all'), CustomerFilingStatusSchema]).default('all'),
  customerWorkflow: CustomerWorkflowFilterSchema.default('all'),
  sort: z
    .enum([
      'updated_desc',
      'updated_asc',
      'customer_code_asc',
      'customer_code_desc',
      'name_asc',
      'name_desc',
      'owner_asc',
    ])
    .default('updated_desc'),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
})
export type ListCustomersParams = z.input<typeof ListCustomersParamsSchema>

export type ListCustomersResult = {
  data: CustomerProfileContract[]
  meta: ContractSourceMeta & {
    total: number
    page: number
    limit: number
  }
}

export type SalesUserContract = SalesUser & {
  sourceMeta: ContractSourceMeta
}

export type CustomerWorkflowStatusContract = {
  applicationStatus: ApplicationStatus
  customerRecordStatus?: CustomerRecordStatus
  customerFilingStatus?: CustomerFilingStatus
  sourceMeta: ContractSourceMeta
}
